import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { Heart, ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Support() {
    const { t } = useT();
    const allocation = t("support.allocation") || [];
    return (
        <section
            id="sostieni"
            data-testid="sn-support"
            className="relative bg-sn-black text-sn-silver py-28 md:py-44 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial opacity-70" />
            <div className="absolute inset-0 hero-grid opacity-40" />
            <div className="grain" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-green">
                        {t("support.eyebrow")}
                    </span>
                    <span className="hairline-dark flex-1" />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-6">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]"
                        >
                            {t("support.headline_a")}{" "}
                            <em className="italic text-sn-green">
                                {t("support.headline_b")}
                            </em>
                            .
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                            className="mt-8 max-w-xl text-lg text-sn-fog leading-relaxed"
                        >
                            {t("support.body")}
                        </motion.p>

                        <a
                            href="https://www.kickstarter.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="sn-support-cta"
                            className="btn-sweep mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-sn-green text-white font-mono-e text-[11px] uppercase tracking-[0.24em] transition-colors duration-300"
                            style={{ ["--tw-sweep"]: "var(--sn-blue)" }}
                        >
                            <Heart size={15} className="fill-current" />
                            {t("support.cta")}
                            <ArrowUpRight size={16} />
                        </a>

                        {/* Allocation */}
                    </div>

                    {/* Fund allocation */}
                    <div className="lg:col-span-6">
                        <div className="font-mono-e text-[11px] uppercase tracking-[0.28em] text-sn-fog mb-6">
                            {t("support.allocationTitle")}
                        </div>
                        <div className="space-y-5">
                            {allocation.map((a, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-sm text-sn-silver">
                                            {a.label}
                                        </span>
                                        <span className="font-mono-e text-xs text-sn-green">
                                            {a.pct}
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{ width: a.pct }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1, delay: i * 0.08, ease: EASE }}
                                            className="h-full bg-sn-green"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
